import React, { useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useState } from "react";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import CreatePortalLink from "./CreatePortalLink";
import PortalLinksDataTable from "components/TableContainers/PortalLinksDataTable";
import { deletePortalLink, updatePortalLink } from "store/actions";

const PortalLinksTable = ({ list, loading, fieldErrors, totalrows, refreshTrigger }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(false);

  const permissions = JSON.parse(localStorage?.getItem('permissions')) || [];
  const isAdmin = Cookies.get('isAdmin') === "yes";
  const hasEditPermission = isAdmin || permissions.includes('portallinks.update');
  const hasDeletePermission = isAdmin || permissions.includes('portallinks.delete');

  // DELETE HANDLER
  const handleDelete = (id) => {
    dispatch(deletePortalLink(id));
    setDeleteId(null);
    setOpenModal(false);
    setConfirmAction(false);
  };

  useEffect(() => {
    if (deleteId && confirmAction) {
      handleDelete(deleteId);
    }
  }, [deleteId, confirmAction]);

  // COLUMNS
  const columns = useMemo(
    () => [
      {
        header: "Title (EN)",
        accessorKey: "title",
        cell: ({ getValue }) => (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
            {getValue()}
          </div>
        ),
      },
      {
        header: "Title (AR)",
        accessorKey: "title_ar",
        cell: ({ getValue }) => (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
            {getValue()}
          </div>
        ),
      },
      {
        header: "Link",
        accessorKey: "link",
        cell: ({ getValue }) => (
          <a href={getValue()} target="_blank" rel="noopener noreferrer">
            {getValue()}
          </a>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => (
          <span className={`badge ${getValue() === 1 ? 'bg-success' : 'bg-danger'}`}>
            {getValue() === 1 ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          const handleEdit = () => {
            setEditData(row.original);
            setIsOpen(true);
          };

          return (
            <div className="d-flex gap-2">
              {hasEditPermission && (
                <Button color="primary" onClick={handleEdit}>
                  <FaRegEdit size={18} />
                </Button>
              )}
              {hasDeletePermission && (
                <Button
                  color="danger"
                  onClick={() => {
                    setDeleteId(row.original.id);
                    setOpenModal(true);
                  }}
                >
                  <MdDeleteOutline size={18} />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [hasEditPermission, hasDeletePermission, t]
  );

  const handleSubmit = (formData, id, resetForm, handleClose) => {
    dispatch(updatePortalLink(formData, id, resetForm, handleClose));
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditData(null);
  };

  return (
    <>
      <ConfirmationModal
        okText={t("Confirm")}
        onCancel={() => {
          setDeleteId(null);
          setOpenModal(false);
        }}
        onOk={() => setConfirmAction(true)}
        isVisible={openModal}
        title={t("Delete Portal Link")}
        content={t("Are you sure you want to delete this portal link?")}
      />

      <CreatePortalLink
        loading={loading}
        fieldErrors={fieldErrors}
        visible={isOpen}
        initialData={editData}
        onSubmit={handleSubmit}
        handleClose={handleClose}
      />

      <div className="container-fluid">
        <PortalLinksDataTable
          loading={loading}
          columns={columns}
          data={list || []}
          isPagination={true}
          totalrows={totalrows}
          refreshTrigger={refreshTrigger}
          SearchPlaceholder={t("Search portal links")}
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />
      </div>
    </>
  );
};

export default PortalLinksTable;
